import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InputContainer } from './styles';
import { Form, Spinner } from 'react-bootstrap';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '~/services/api';
import useDebounce from '~/hooks/useDebounce';
import { Lista } from './Lista';
import { InputIbsCbsProps } from './protocols';

export const InputIbsCbs: React.FC<InputIbsCbsProps> = ({
  label = '',
  placeholder = 'Selecione uma Classificação',
  ListaWidth = '100%',
  name,
  register,
  isError,
  control,
  changeSelected,
  hideNoneOption = false,
  valueRetriever = null,
  reset = false,
  ...rest
}: InputIbsCbsProps) => {
  /**
   * debouncedFn
   * Hook de Debounce usado para efetuar a busca após digitação do usuário
   */
  const { debouncedFn } = useDebounce();
  /**
   * inputRef
   * Ref do input utilizado para setar a label da opção selecionada no input
   */
  const inputRef: any = useRef(null);
  /**
   * selected, setSelected
   * Opção selecionada da lista
   */
  const [selected, setSelected] = useState<any>({
    label: undefined,
    value: undefined,
  });

  /**
   * isOpen
   * Renderiza ou não a lista
   */
  const [isOpen, setIsOpen] = useState<boolean>(false);
  /**
   * filter
   * Filtro passado para a api
   */
  const [filter, setFilter] = useState<string>('');

  /**
   * limit
   * Quantidade de registros buscados por vez na api
   */
  const limit = 10;
  /**
   * useInfiniteQuery
   * Hook do react-query para listagem infinita de dados
   */
  const queryClient = useQueryClient();
  const { data, hasNextPage, fetchNextPage, isFetching, refetch } =
    useInfiniteQuery(name, ({ pageParam = 1 }) => fetchData({ pageParam }), {
      refetchOnWindowFocus: false,
    });

  /**
   * fetchData
   * Método que se comunica com a api
   */
  const filterOptions = useCallback(
    (options: any[]): any[] => {
      const filteredOptions = options.filter((item) =>
        String(item.descricao)
          .toLowerCase()
          .includes(String(filter).toLowerCase()),
      );
      return filteredOptions;
    },
    [filter],
  );

  const fetchData = useCallback(
    async ({ pageParam = 1 }): Promise<any> => {
      try {
        const { data: dataFields } = await api.get(
          '/classificacao-ibs-cbs/getClassificacao',
          {
            params: {
              page: pageParam,
              limit,
              filter,
            },
          },
        );

        const options = filterOptions(dataFields.data)
          .filter((d: any) => d.flg_inativo !== true)
          .map((dt: any) => {
            return {
              value: dt.cod_ibs_cbs,
              label: `${dt.descricao}`,
              ...dt,
            };
          });
        if (!hideNoneOption) {
          options.unshift({
            value: null,
            label: 'NENHUM(A)',
          });
        }
        queryClient.setQueryData(`cache_classif_ibs_cbs`, {
          ...dataFields,
          data: options,
        });

        return {
          ...dataFields,
          data: options,
        };
      } catch (error: any) {
        if (error.data?.message) {
          toast.error(error.data.message);
        } else {
          toast.error(String(error));
        }
        return {
          count: '0',
          data: [],
          fields: [],
          pagination: {
            lastPage: 0,
            prevPage: 1,
            startPage: 1,
          },
          success: true,
        };
      }
    },
    [filter, filterOptions, hideNoneOption, queryClient],
  );

  useEffect(() => {
    const run = async () => {
      if (typeof valueRetriever !== 'number') return;

      const cached: any = queryClient.getQueryData('cache_classif_ibs_cbs');
      let found =
        cached?.data?.find((o: any) => o?.value === valueRetriever) ?? null;

      try {
        if (!found) {
          const { data: listResp } = await api.get(
            '/classificacao-ibs-cbs/getClassificacao',
            { params: { page: 1, limit: 50, filter: '' } },
          );

          const options =
            (listResp?.data ?? [])
              .filter((d: any) => d.flg_inativo !== true)
              .map((dt: any) => ({
                value: dt.cod_ibs_cbs,
                label: `${dt.descricao}`,
                ...dt,
              })) ?? [];

          found = options.find((o: any) => o.value === valueRetriever) ?? null;

          if (found) {
            queryClient.setQueryData('cache_classif_ibs_cbs', {
              ...listResp,
              data: options,
            });
          }
        }
      } catch (error: any) {
        toast.error(error.data.message);
      }

      if (found) {
        setSelected(found);
        if (inputRef.current) {
          inputRef.current.value = '';
          inputRef.current.placeholder = found.label;
        }
        setIsOpen(false);
        if (typeof changeSelected === 'function') changeSelected(found);
      }
    };

    run();
  }, [valueRetriever, queryClient]);

  useEffect(() => {
    if (!reset && valueRetriever) return;

    setSelected({ label: undefined, value: undefined });

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.placeholder = placeholder;
    }

    setIsOpen(false);
    setFilter('');

    try {
      queryClient.removeQueries(name);
    } catch {
      /* noop */
    }

    if (typeof changeSelected === 'function') {
      changeSelected({
        cod_cst_ibs_cbs: 0,
        cod_ibs_cbs: 0,
        cst_ibs_cbs: 0,
        des_classif_trib: 0,
        des_ibs_cbs: '',
        label: '',
        per_aliq_cbs: 0,
        per_red_cbs: 0,
        val_red_social: 0,
        value: 0,
      });
    }
  }, [reset, placeholder, name, queryClient, valueRetriever]);

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field: { value } }) => {
            if (inputRef && inputRef.current) {
              /** A condicional abaixo limpa o input */
              if (value) {
                if (
                  value.label !== selected.label &&
                  inputRef.current.placeholder !== placeholder
                ) {
                  inputRef.current.placeholder = placeholder;
                }

                if (value.label) {
                  inputRef.current.placeholder = `${value.label}`;
                }
                if (!value.label) {
                  inputRef.current.placeholder = placeholder;
                }
              } else {
                inputRef.current.placeholder = placeholder;
              }
            }
            return (
              <>
                <div className="select-container">
                  <label>
                    <input
                      {...register(name)}
                      type="text"
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      placeholder={placeholder}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        event.target.value = event.target.value.toUpperCase();
                        setFilter(event.target.value.toUpperCase());
                        if (event.target.value === '') {
                          queryClient.removeQueries(name);
                        }
                        debouncedFn(() => refetch(), 500);
                      }}
                      autoComplete="off"
                      onClick={(event: any) => {
                        if (selected.label) {
                          event.target.placeholder = selected.label;
                        }
                        event.target.value = '';
                        setIsOpen(true);
                      }}
                      ref={inputRef}
                      {...rest}
                    />
                    <div className="drop-indicator">
                      <span role="img" aria-label="open">
                        {filter && isFetching ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="spinner"
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            role="presentation"
                          >
                            <path
                              d="M8.292 10.293a1.009 1.009 0 000 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 000-1.419.987.987 0 00-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 00-1.406 0z"
                              fill="currentColor"
                              fillRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </label>
                </div>
                {isOpen && (
                  <Lista
                    listaWidth={ListaWidth}
                    inputRef={inputRef}
                    setIsOpen={setIsOpen}
                    setFilter={setFilter}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    data={data}
                    selected={selected}
                    setSelected={setSelected}
                    isFetching={isFetching}
                    queryName={name}
                    changeSelected={changeSelected}
                    queryClient={queryClient}
                  />
                )}
              </>
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
